import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { Close, Description } from "@mui/icons-material";

import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import RichTextEditor from "./rich-text-editor";

const RESUMO_MAX_LENGTH = 1000;

const STATUS = {
  RASCUNHO: "RASCUNHO",
  AGENDADO: "AGENDADO",
  PARCIALMENTE_PUBLICADO: "PARCIALMENTE_PUBLICADO",
  PUBLICADO: "PUBLICADO",
};

export default function DialogCreateEdit({
  open,
  onClose,
  refresh,
  selectedNota,
}) {
  const snackbar = useSnackbar();
  const isCreate = !selectedNota;

  const [salvando, setSalvando] = useState(false);
  const [nota, setNota] = useState(selectedNota);

  const [versao, setVersao] = useState(selectedNota?.Versao || "");
  const [titulo, setTitulo] = useState(selectedNota?.Titulo || "");
  const [resumoPush, setResumoPush] = useState(selectedNota?.ResumoPush || "");
  const [conteudoHtml, setConteudoHtml] = useState(
    selectedNota?.ConteudoHtml || "",
  );
  const [publicarAndroid, setPublicarAndroid] = useState(
    selectedNota?.PublicarAndroid || true,
  );
  const [publicarIos, setPublicarIos] = useState(
    selectedNota?.PublicarIos || true,
  );

  const status = nota?.Status || STATUS.RASCUNHO;
  // Título/resumo/conteúdo podem ser corrigidos a qualquer momento (mesmo já publicada — editar
  // não reenvia o push, só atualiza o que a página pública mostra dali pra frente). Versão e as
  // checkboxes de loja ficam travadas assim que sai de RASCUNHO: mudar a versão depois de agendada
  // quebraria a âncora pública e o matching com as lojas.
  const versaoEPlataformasBloqueadas = status !== STATUS.RASCUNHO;

  // Nada é salvo só por abrir o dialog — o rascunho só existe no banco a partir do primeiro
  // "Salvar rascunho" (ou "Agendar publicação", se o usuário pular direto pra lá) clicado de
  // verdade. Antes disso, "nota" é null e o editor de imagem já lida com isso sozinho (mostra um
  // aviso "salve a nota antes de inserir imagens" em vez de tentar subir sem id).
  const salvarConteudo = async () => {
    const payload = { versao, titulo, resumoPush, conteudoHtml };
    const res = nota
      ? await Api.put(
          "update",
          "releaseNotesDigitalApi",
          { idNotaAtualizacao: nota.IdNotaAtualizacao, ...payload },
          true,
        )
      : await Api.post("create", "releaseNotesDigitalApi", payload, true);
    return res?.Result;
  };

  const handleSalvarRascunho = async () => {
    setSalvando(true);
    try {
      const notaSalva = await salvarConteudo();
      setNota(notaSalva);
      snackbar.showMessage("Rascunho salvo com sucesso.", {
        severity: "success",
      });
      refresh();
    } catch (error) {
      snackbar.showMessage(error?.toString() || "Erro ao salvar rascunho", {
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  // Corrige título/resumo/conteúdo de uma nota já agendada ou publicada — não muda versão nem as
  // lojas marcadas (campos travados na UI nesse ponto) e não reenvia push nenhum.
  const handleSalvarEdicao = async () => {
    setSalvando(true);
    try {
      const notaSalva = await salvarConteudo();
      setNota(notaSalva);
      snackbar.showMessage("Alterações salvas com sucesso.", {
        severity: "success",
      });
      refresh();
    } catch (error) {
      snackbar.showMessage(error?.toString() || "Erro ao salvar alterações", {
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  const handleAgendar = async () => {
    setSalvando(true);
    try {
      // Garante que o conteúdo mais recente foi salvo (criando o rascunho agora, se ainda não existir).
      const notaSalva = await salvarConteudo();

      const res = await Api.post(
        "agendar",
        "releaseNotesDigitalApi",
        {
          idNotaAtualizacao: notaSalva.IdNotaAtualizacao,
          publicarAndroid,
          publicarIos,
        },
        true,
      );
      setNota(res?.Result);
      snackbar.showMessage(
        "Nota agendada. Cada loja publica sozinha, assim que liberar essa versão — Android e iOS não esperam um pelo outro.",
        { severity: "success", autoHideDuration: 8000 },
      );
      refresh();
    } catch (error) {
      snackbar.showMessage(error?.toString() || "Erro ao agendar publicação", {
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  const handleCancelarAgendamento = async () => {
    setSalvando(true);
    try {
      const res = await Api.post(
        `cancelar-agendamento?id=${nota.IdNotaAtualizacao}`,
        "releaseNotesDigitalApi",
        {},
        true,
      );
      setNota(res?.Result);
      snackbar.showMessage(
        "Agendamento cancelado — nota voltou para rascunho.",
        {
          severity: "success",
        },
      );
      refresh();
    } catch (error) {
      snackbar.showMessage(
        error?.toString() || "Erro ao cancelar agendamento",
        {
          severity: "error",
        },
      );
    } finally {
      setSalvando(false);
    }
  };

  const isFormValido = versao && titulo && resumoPush && conteudoHtml;
  const isAgendamentoValido = isFormValido && (publicarAndroid || publicarIos);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3" color="primary">
            <Description style={{ marginRight: "12px" }} />
            {isCreate ? "Nova nota de atualização" : "Nota de atualização"}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
            {status !== STATUS.RASCUNHO && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {status === STATUS.AGENDADO &&
                    "Agendada — cada loja publica sozinha, assim que liberar essa versão. Não existe botão de publicar manual."}
                  {(status === STATUS.PARCIALMENTE_PUBLICADO ||
                    status === STATUS.PUBLICADO) && (
                    <>
                      {publicarAndroid && (
                        <span style={{ display: "block" }}>
                          Play Store:{" "}
                          {nota.DataPublicacaoAndroid
                            ? `publicado em ${nota.DataPublicacaoAndroid}`
                            : "aguardando a Play Store liberar essa versão"}
                        </span>
                      )}
                      {publicarIos && (
                        <span style={{ display: "block" }}>
                          App Store:{" "}
                          {nota.DataPublicacaoIos
                            ? `publicado em ${nota.DataPublicacaoIos}`
                            : "aguardando a App Store liberar essa versão"}
                        </span>
                      )}
                      <span style={{ display: "block", marginTop: 4 }}>
                        Versão e lojas não podem mais mudar, mas título,
                        resumo e conteúdo podem ser corrigidos a qualquer
                        momento (não reenvia o push).
                      </span>
                    </>
                  )}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <FormGroup>
                <FormLabel>Versão *</FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={versao}
                  onChange={(e) => setVersao(e.target.value)}
                  disabled={versaoEPlataformasBloqueadas}
                  helperText="ex.: 1.4.0 — a mesma versão vale pra Android e iOS"
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormGroup
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  height: "100%",
                }}
              >
                <FormLabel>Onde publicar *</FormLabel>
                <Box display="flex" alignItems="center" justifyContent="start">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={publicarAndroid}
                        onChange={(e) => setPublicarAndroid(e.target.checked)}
                        color="primary"
                        disabled={versaoEPlataformasBloqueadas}
                      />
                    }
                    label="Play Store (Android)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={publicarIos}
                        onChange={(e) => setPublicarIos(e.target.checked)}
                        color="primary"
                        disabled={versaoEPlataformasBloqueadas}
                      />
                    }
                    label="App Store (iOS)"
                  />
                </Box>
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <FormGroup>
                <FormLabel>Título *</FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  helperText="ex.: Versão 1.4.0"
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <FormGroup>
                <FormLabel>
                  Resumo (aparece na notificação e na lista) *
                </FormLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={2}
                  value={resumoPush}
                  onChange={(e) =>
                    setResumoPush(e.target.value.slice(0, RESUMO_MAX_LENGTH))
                  }
                  helperText={`${resumoPush.length}/${RESUMO_MAX_LENGTH} — Resumo curto das novidades desta versão`}
                />
              </FormGroup>
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Notas completas (aparece na página pública)</FormLabel>
              <Box mt={1}>
                <RichTextEditor
                  value={conteudoHtml}
                  onChange={setConteudoHtml}
                  idNotaAtualizacao={nota?.IdNotaAtualizacao}
                />
              </Box>
            </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button component={Box} onClick={onClose}>
          Fechar
        </Button>

        {status === STATUS.RASCUNHO && (
          <>
            <Button
              variant="outlined"
              onClick={handleSalvarRascunho}
              disabled={salvando || !isFormValido}
            >
              Salvar rascunho
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAgendar}
              disabled={salvando || !isAgendamentoValido}
            >
              Agendar publicação
            </Button>
          </>
        )}

        {status !== STATUS.RASCUNHO && (
          <Button
            variant="outlined"
            onClick={handleSalvarEdicao}
            disabled={salvando || !isFormValido}
          >
            Salvar alterações
          </Button>
        )}

        {status === STATUS.AGENDADO && (
          <Button
            variant="outlined"
            onClick={handleCancelarAgendamento}
            disabled={salvando}
          >
            Cancelar agendamento
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
